import { useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getLoginRoleOptions, isAdminLoginAllowed } from '../utils/loginRoles';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { Toolbar } from '../components/ui/Toolbar';
import { AppLogoWordmark } from '../components/ui/AppLogo';
import { LoginContactSupport } from '../components/LoginContactSupport';
import { LoginCredit } from '../components/LoginCredit';
import { LoginTermsNotice } from '../components/LoginTermsNotice';
import { LoginErrorReport } from '../components/LoginErrorReport';
import { PwaInstallPrompt } from '../components/pwa/PwaInstallPrompt';
import { buildLoginErrorReport } from '../utils/loginErrorReport';

export function LoginPage() {
  const {
    role,
    setRole,
    loginData,
    setLoginData,
    login,
    loginError,
    loginErrorMeta,
    isLoading,
    labels,
    setUseMalayalam,
    useMalayalam,
  } = useApp();

  const roleOptions = getLoginRoleOptions(labels);

  useEffect(() => {
    if (role === 'admin' && !isAdminLoginAllowed()) {
      setRole('head');
    }
  }, [role, setRole]);

  // Prefill floating support FAB with the same full report when login failed.
  const supportReportText = loginError
    ? buildLoginErrorReport({
        role,
        username: loginData.username,
        errorMessage: loginError,
        errorCode: loginErrorMeta?.errorCode,
        httpStatus: loginErrorMeta?.httpStatus,
        axiosCode: loginErrorMeta?.axiosCode,
        rawMessage: loginErrorMeta?.rawMessage,
        useMalayalam,
      })
    : '';

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-x-hidden bg-surface px-4 py-8 pb-28">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary-light/70 via-surface to-surface dark:from-card dark:via-surface dark:to-surface"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-primary/25 blur-3xl dark:bg-primary/20 dark:opacity-80"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-primary-light blur-3xl dark:bg-primary/10 dark:opacity-60"
        aria-hidden
      />

      <div className="relative z-10 mb-6 w-full max-w-md text-center">
        <AppLogoWordmark className="mx-auto mb-2 h-auto w-full max-h-28 max-w-[18rem] object-contain sm:max-h-36 sm:max-w-[22rem]" />
        <p className="mt-2 text-xs text-muted sm:text-sm">School NM present management</p>
      </div>

      <Card className="relative z-10 w-full max-w-md dark:border-border/80 dark:shadow-card">
        <CardBody className="space-y-4">
          <Toolbar>
            <ThemeToggle useMalayalam={useMalayalam} compact />
            <button
              type="button"
              onClick={() => setUseMalayalam(!useMalayalam)}
              className="text-xs font-medium text-primary hover:underline sm:text-sm"
            >
              {labels.toggleLang}
            </button>
          </Toolbar>

          <div>
            <p className="mb-1.5 text-xs font-medium text-text sm:text-sm">{labels.selectRole}</p>
            <SegmentedControl options={roleOptions} value={role} onChange={setRole} />
          </div>

          <Input
            label={labels.username}
            labelClassName="text-xs sm:text-sm"
            id="login-username"
            name="username"
            value={loginData.username}
            onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
            autoComplete="username"
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder={role === 'teacher' ? 'head1_2A' : undefined}
          />
          {role === 'teacher' && (
            <p className="-mt-2 text-xs text-muted">{labels.teacherLoginHint}</p>
          )}
          <Input
            label={labels.password}
            labelClassName="text-xs sm:text-sm"
            id="login-password"
            name="password"
            type="password"
            value={loginData.password}
            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            autoComplete="current-password"
            onKeyDown={(e) => e.key === 'Enter' && login()}
          />

          <LoginTermsNotice labels={labels} useMalayalam={useMalayalam} />

          <Button
            className="w-full"
            size="md"
            onClick={login}
            isLoading={isLoading.login}
            disabled={!role}
          >
            {labels.login}
          </Button>

          {/* After Sign in so failed attempt → report sits under the thumb */}
          {loginError && (
            <LoginErrorReport
              labels={labels}
              role={role}
              username={loginData.username}
              errorMessage={loginError}
              errorCode={loginErrorMeta?.errorCode}
              httpStatus={loginErrorMeta?.httpStatus}
              axiosCode={loginErrorMeta?.axiosCode}
              rawMessage={loginErrorMeta?.rawMessage}
              useMalayalam={useMalayalam}
            />
          )}
        </CardBody>
      </Card>

      <LoginCredit labels={labels} />
      <LoginContactSupport labels={labels} reportText={supportReportText} />
      <PwaInstallPrompt labels={labels} />
    </div>
  );
}
